import {
	LockOutlined,
	MailOutlined,
	UserAddOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Alert, Button, Checkbox, Form, Input } from "antd";
import React, { useEffect, useState } from "react";
import { addUser, updateUser } from "../../services/userService";

const UserForm = ({ onUserAdded, onUserUpdated, userToEdit }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const isMobile = window.innerWidth < 768;

	// Cargar datos del usuario si estamos en modo edición
	useEffect(() => {
		if (userToEdit) {
			form.setFieldsValue({
				firstName: userToEdit.firstName,
				lastName: userToEdit.lastName,
				username: userToEdit.username,
				email: userToEdit.email,
				Admin: userToEdit.Admin || false,
			});
			setIsEditing(true);
		} else {
			form.resetFields();
			setIsEditing(false);
		}
	}, [userToEdit, form]);

	const onFinish = async (values) => {
		try {
			// Limpiar error previo
			setError("");
			setLoading(true);

			if (isEditing) {
				// Crear objeto de datos de usuario para actualizar
				const userData = {
					firstName: values.firstName,
					lastName: values.lastName,
					username: values.username,
					email: values.email,
					Admin: values.Admin || false,
					pendingSync: 1,
				};

				// Si se proporciona una nueva contraseña al editar, incluirla en la actualización
				if (values.password) {
					// Validar que las contraseñas coincidan
					if (values.password !== values.confirmPassword) {
						setError("Las contraseñas no coinciden");
						setLoading(false);
						return;
					}
					userData.password = values.password;
				}

				// Actualizar usuario existente
				const updatedUser = await updateUser(userToEdit.id, userData);

				// Mostrar mensaje de éxito
				window.messageApi.success("Usuario actualizado correctamente");

				// Notificar al componente padre
				if (onUserUpdated) onUserUpdated(updatedUser);
			} else {
				// Validar que las contraseñas coincidan
				if (values.password !== values.confirmPassword) {
					setError("Las contraseñas no coinciden");
					setLoading(false);
					return;
				}

				// Guardar usuario en IndexedDB
				const result = await addUser({
					firstName: values.firstName,
					lastName: values.lastName,
					username: values.username,
					email: values.email,
					password: values.password,
					Admin: values.Admin || false,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Usuario registrado correctamente");

				// Limpiar formulario
				form.resetFields();

				// Notificar al componente padre
				if (onUserAdded) onUserAdded(result);
			}
		} catch (error) {
			// Mostrar error específico
			setError(error.message || "Error al procesar el usuario");
			console.error("Error al procesar usuario:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setIsEditing(false);
		if (onUserUpdated) onUserUpdated(null);
	};

	return (
		<div className="user-form-container">
			<h2>{isEditing ? "Editar Usuario" : "Registro de Usuario"}</h2>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Form
				form={form}
				name="register"
				onFinish={onFinish}
				layout="vertical"
				scrollToFirstError
			>
				<div style={{ display: isMobile ? "block" : "flex", gap: "10px" }}>
					<Form.Item
						style={{ width: "100%" }}
						name="firstName"
						label="Nombre"
						rules={[
							{
								required: true,
								message: "Por favor ingrese su nombre",
							},
						]}
					>
						<Input prefix={<UserOutlined />} placeholder="Ingrese su nombre" />
					</Form.Item>

					<Form.Item
						style={{ width: "100%" }}
						name="lastName"
						label="Apellidos"
						rules={[
							{
								required: true,
								message: "Por favor ingrese sus apellidos",
							},
						]}
					>
						<Input
							prefix={<UserOutlined />}
							placeholder="Ingrese sus apellidos"
						/>
					</Form.Item>
				</div>

				<Form.Item
					name="username"
					label="Nombre de usuario"
					rules={[
						{
							required: true,
							message: "Por favor ingrese un nombre de usuario",
						},
					]}
				>
					<Input
						prefix={<UserAddOutlined />}
						placeholder="Elija un nombre de usuario"
					/>
				</Form.Item>

				<Form.Item
					name="email"
					label="Correo electrónico"
					rules={[
						{
							type: "email",
							message: "Ingrese un correo electrónico válido",
						},
						{
							required: true,
							message: "Por favor ingrese su correo electrónico",
						},
					]}
				>
					<Input
						prefix={<MailOutlined />}
						placeholder="Ingrese su correo electrónico"
					/>
				</Form.Item>

				<Form.Item
					name="Admin"
					valuePropName="checked"
				>
					<Checkbox>
						Administrador
					</Checkbox>
				</Form.Item>

				<div style={{ display: isMobile ? "block" : "flex", gap: "10px" }}>
					<Form.Item
						style={{ width: "100%" }}
						name="password"
						label="Contraseña"
						rules={[
							{
								required: !isEditing,
								message: "Por favor ingrese su contraseña",
							},
						]}
						hasFeedback
					>
						<Input.Password
							prefix={<LockOutlined />}
							placeholder="Ingrese su contraseña"
						/>
					</Form.Item>

					<Form.Item
						style={{ width: "100%" }}
						name="confirmPassword"
						label="Confirmar contraseña"
						dependencies={["password"]}
						hasFeedback
						rules={[
							{
								required: !isEditing,
								message: "Por favor confirme su contraseña",
							},
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || getFieldValue("password") === value) {
										return Promise.resolve();
									}
									return Promise.reject(
										new Error("Las contraseñas no coinciden")
									);
								},
							}),
						]}
					>
						<Input.Password
							prefix={<LockOutlined />}
							placeholder="Confirme su contraseña"
						/>
					</Form.Item>
				</div>

				<Form.Item>
					<div style={{ display: "flex", gap: "10px" }}>
						<Button type="primary" htmlType="submit" loading={loading} block>
							{isEditing ? "Actualizar Usuario" : "Registrar Usuario"}
						</Button>

						{isEditing && (
							<Button onClick={handleCancel} block>
								Cancelar
							</Button>
						)}
					</div>
				</Form.Item>
			</Form>
		</div>
	);
};

export default UserForm;
